/** @file sampleKeys: a sparse key channel sampled at a frame with ease, hold and an optional wrap target. */
import { clamp } from '@zilo/pose3d-std/math/clamp';
import type { Key } from '@zilo/pose3d-pose/sample/channelTypes';
import { easeAt } from '@zilo/pose3d-pose/time/easeAt';
import { holdFrame } from '@zilo/pose3d-pose/time/holdFrame';

/**
 * The value at `frame` of a non-empty channel: the first value before the first key; between keys k0 and k1
 * `lerp(k0, k1, easeAt(k0.ease, (holdFrame(frame) - k0) / (k1 - k0)))`; after the last key it holds when
 * `wrapValue` is null, else runs the same segment toward `{ wrapFrame, wrapValue }` (decision 6).
 */
export function sampleKeys<T>(ch: Key<T>[], frame: number, wrapFrame: number, wrapValue: T | null, lerp: (a: T, b: T, t: number) => T): T {
  let i = 0;
  while (i + 1 < ch.length && (ch[i + 1]?.frame ?? Infinity) <= frame) i++;
  const k0 = ch[i];
  if (k0 === undefined) throw new RangeError('sampleKeys: empty channel');
  if (frame <= k0.frame) return k0.value;
  const k1 = ch[i + 1];
  const toFrame = k1 === undefined ? wrapFrame : k1.frame;
  const toValue = k1 === undefined ? wrapValue : k1.value;
  if (toValue === null || toFrame <= k0.frame) return k0.value;
  const at = holdFrame(frame, k0.frame, k0.hold);
  return lerp(k0.value, toValue, easeAt(k0.ease, clamp((at - k0.frame) / (toFrame - k0.frame), 0, 1)));
}
